import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { FullClient, SearchClient, UpsertClientBody, UpsertClientPermissionsBody } from "./types";

export const useClientsApi = () => {
  const api = useApi();

  // Get a single client
  const getAdminClient = (id: string, signal: AbortSignal) => {
    return useSWRMutation<FullClient | null, CustomError, string>(`/admin/clients/${id}`, (url) =>
      api.get<FullClient>({ url, signal }),
    );
  };

  // Get a paginated list of clients
  const useAdminClients = (query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl("/admin/clients", query);
    return useSWR<Paginated<SearchClient> | null, CustomError, string>(url, (url) =>
      api.get<Paginated<SearchClient>>({ url, signal }),
    );
  };

  // Insert a new client
  const insertAdminClient = useCallback(async (body: UpsertClientBody): Promise<SearchClient> => {
    const url = `/admin/clients`;
    const item = await api.post<UpsertClientBody, SearchClient>(url, body);
    api.mutateCache(["/admin/clients"]);
    return item;
  }, []);

  // Update an existing client
  const updateAdminClient = useCallback(async (itemId: string, body: UpsertClientBody): Promise<void> => {
    const url = `/admin/clients/${itemId}`;
    await api.put<UpsertClientBody>(url, body);
    api.mutateCache(["/admin/clients"], url);
  }, []);

  // Delete a client
  const deleteAdminClient = useCallback(async (clientId: string): Promise<void> => {
    await api.del(`/admin/clients/${clientId}`).then(() => {
      api.mutateCache(["/admin/clients", `/admin/clients/${clientId}`]);
    });
  }, []);

  const upsertAdminClientPermissions = useCallback(
    async (itemId: string, body: UpsertClientPermissionsBody): Promise<void> => {
      const url = `/admin/clients/${itemId}/permissions`;
      await api.put<UpsertClientPermissionsBody>(url, body);
      api.mutateCache([url, "/admin/clients"], url);
    },
    [],
  );

  return {
    getAdminClient,
    useAdminClients,
    insertAdminClient,
    updateAdminClient,
    deleteAdminClient,
    upsertAdminClientPermissions,
  };
};
